/* eslint-disable complexity */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';
import { useSidebarState } from 'react-admin';

// Material-UI Icons
import UpdateIcon from '@mui/icons-material/Update';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DescriptionIcon from '@mui/icons-material/Description';
import PolicyIcon from '@mui/icons-material/Policy';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import EventIcon from '@mui/icons-material/Event';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ImageIcon from '@mui/icons-material/Image';
import CampaignIcon from '@mui/icons-material/Campaign';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import SearchIcon from '@mui/icons-material/Search';
import RedoIcon from '@mui/icons-material/Redo';
import InboxIcon from '@mui/icons-material/Inbox';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CategoryIcon from '@mui/icons-material/Category';
import ShareIcon from '@mui/icons-material/Share';
import PaletteIcon from '@mui/icons-material/Palette';
import PaymentsIcon from '@mui/icons-material/Payments';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import MovieIcon from '@mui/icons-material/Movie';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ApartmentIcon from '@mui/icons-material/Apartment';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DownloadIcon from '@mui/icons-material/Download';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PlaceIcon from '@mui/icons-material/Place';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import StarIcon from '@mui/icons-material/Star';
import GroupsIcon from '@mui/icons-material/Groups';
import BadgeIcon from '@mui/icons-material/Badge';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DevicesIcon from '@mui/icons-material/Devices';
import ShieldIcon from '@mui/icons-material/Shield';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { Taxonomies } from '@src/global/taxonomies';
import { Permissions } from '@src/global/permissions';

import Logo from './Logo';
import NavSearch from './NavSearch';
import NavAccountFooter from './NavAccountFooter';
import SubMenu from './SubMenu';
import {
    StyledMenuItemLink,
    StyledSubMenuItemLink,
    SectionHeader,
    // NavBadge,
    MenuSection,
} from './MenuStyles';

const UNAUTHORIZED_PATH = '/unauthorized';
const PREFERRED_LANDING_PATH = '/featured_content';

export const buildMenuSections = () => {
    // Navigation sections definition
    const sections = [
        {
            key: 'content',
            title: 'Content',
            items: [
                {
                    key: 'product-updates',
                    title: 'Product news and updates',
                    to: '/product_updates',
                    icon: <UpdateIcon />,
                    visible: Permissions('product_updates', 'read') === true,
                },
                {
                    key: 'alerts',
                    title: 'Alerts',
                    to: '/alerts',
                    icon: <NotificationsIcon />,
                    // badge: <NavBadge tone="info">{alertsCount}</NavBadge>,
                    visible: Permissions('alerts', 'read') === true,
                },
                {
                    key: 'underwriting-guidelines',
                    title: 'Underwriting guidelines',
                    to: '/underwriting_guidelines',
                    icon: <MenuBookIcon />,
                    visible: Permissions('underwriting_guidelines', 'read') === true,
                },
                {
                    key: 'rate-bulletins',
                    title: 'Rate bulletins',
                    to: '/rate_bulletins',
                    icon: <DescriptionIcon />,
                    visible: Permissions('rate_bulletins', 'read') === true,
                },
                {
                    key: 'impacted-policies',
                    title: 'Policy updates',
                    to: '/impacted_policies',
                    icon: <PolicyIcon />,
                    visible: Permissions('impacting_policies', 'read') === true,
                },
                {
                    key: 'trainings',
                    title: 'eLearning',
                    to: '/trainings',
                    icon: <SchoolIcon />,
                    visible: Permissions('trainings', 'read') === true,
                },
                {
                    key: 'job-aids',
                    title: 'Job aids',
                    to: '/job_aids',
                    icon: <ArticleIcon />,
                    visible: Permissions('job_aids', 'read') === true,
                },
                {
                    key: 'webinars',
                    title: 'Live webinars',
                    to: '/webinars',
                    icon: <EventIcon />,
                    visible: Permissions('webinars', 'read') === true,
                },
                {
                    key: 'media_files',
                    title: 'Media files',
                    icon: <PermMediaIcon />,
                    visible: Permissions('images', 'read') === true,
                    isSubMenu: true,
                    subKey: 'media_files',
                    children: [
                        { key: 'featured-images', title: 'Featured images', to: { pathname: '/images', search: `filter=${JSON.stringify({ folder: 'featured' })}` } },
                        { key: 'thumbnail-images', title: 'Thumbnail images', to: { pathname: '/images', search: `filter=${JSON.stringify({ folder: 'thumbnail' })}` } },
                        { key: 'content-images', title: 'Content images', to: { pathname: '/images', search: `filter=${JSON.stringify({ folder: 'content' })}` } },
                        { key: 'attachments', title: 'Attachments', to: '/attachments' },
                    ],
                },
            ],
        },
        {
            key: 'site',
            title: 'Site',
            items: [
                {
                    key: 'homepage',
                    title: 'Homepage',
                    icon: <DashboardIcon />,
                    visible: Permissions('home_articles', 'read') === true || Permissions('home_links', 'read') === true,
                    isSubMenu: true,
                    subKey: 'homepage',
                    children: [
                        Permissions('home_articles', 'read') === true && { key: 'featured-content', title: 'Featured content', to: '/featured_content' },
                        Permissions('home_links', 'read') === true && { key: 'home-links', title: 'Custom links', to: '/home_links' },
                    ].filter(Boolean),
                },
                {
                    key: 'banners',
                    title: 'Banners',
                    to: '/banners',
                    icon: <ImageIcon />,
                    visible: Permissions('promo_notification_settings', 'read') === true,
                },
                {
                    key: 'pop-ups',
                    title: 'Pop-ups',
                    to: '/home_banners',
                    icon: <CampaignIcon />,
                    visible: Permissions('home_banners', 'read') === true,
                },
                {
                    key: 'feed-items',
                    title: 'RSS feed',
                    to: '/feed_items',
                    icon: <RssFeedIcon />,
                    visible: Permissions('feed_items', 'read') === true,
                },
                {
                    key: 'search_settings',
                    title: 'Search',
                    icon: <SearchIcon />,
                    visible: Permissions('search_settings', 'read') === true || Permissions('tags', 'read') === true,
                    isSubMenu: true,
                    subKey: 'search_settings',
                    children: [
                        Permissions('search_settings', 'read') === true && { key: 'pinned-results', title: 'Pinned results', to: '/search_phrases' },
                        Permissions('search_settings', 'read') === true && { key: 'search-weights', title: 'Ranking weights', to: '/search_weights' },
                        Permissions('search_settings', 'read') === true && { key: 'matching-search', title: 'Synonyms and misspellings', to: '/matching_searches' },
                        Permissions('tags', 'read') === true && { key: 'tags', title: 'Search tags', to: '/tags' },
                        Permissions('search_settings', 'read') === true && { key: 'search-terms', title: 'Search terms', to: '/search_terms' },
                    ].filter(Boolean),
                },
                {
                    key: 'redirects',
                    title: 'Redirects',
                    to: '/redirects',
                    icon: <RedoIcon />,
                    visible: Permissions('redirects', 'read') === true,
                },
            ],
        },
        {
            key: 'marketing',
            title: 'Co-op marketing',
            items: [
                {
                    key: 'collateral-requests',
                    title: 'Requests',
                    to: '/collateral_requests',
                    icon: <InboxIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'collateral-templates',
                    title: 'Collateral',
                    to: '/collateral_templates',
                    icon: <DashboardCustomizeIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'collateral-categories',
                    title: 'Collateral categories',
                    to: '/collateral_categories',
                    icon: <CategoryIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'social-media',
                    title: 'Social media',
                    to: '/social_sharings',
                    icon: <ShareIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'branding-assets',
                    title: 'Branding assets',
                    to: '/collateral_digital_assets',
                    icon: <PaletteIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'collateral-logos',
                    title: 'Collateral logos',
                    to: '/collateral_logos',
                    icon: <ImageIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'collateral-prices',
                    title: 'Item pricing',
                    to: '/collateral_prices',
                    icon: <PaymentsIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'template-suggestions',
                    title: 'Template suggestions',
                    to: '/collateral_template_suggestions',
                    icon: <LightbulbIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                },
                {
                    key: 'video-cobranding',
                    title: 'Video co-branding',
                    icon: <MovieIcon />,
                    visible: Permissions('co_branded_videos', 'read') === true,
                    isSubMenu: true,
                    subKey: 'video_cobranding',
                    children: [
                        { key: 'video-templates', title: 'Video catalog', to: '/co_branded_video_templates' },
                        { key: 'video-categories', title: 'Video categories', to: '/co_branded_video_categories' },
                        { key: 'video-brands', title: 'Brands', to: '/brands' },
                        { key: 'video-logos', title: 'Video logos', to: '/co_branded_video_logos' },
                        { key: 'video-requests', title: 'Video requests', to: '/co_branded_video_requests' },
                    ],
                },
                {
                    key: 'mdf-spending',
                    title: 'MDF spending',
                    icon: <AccountBalanceWalletIcon />,
                    visible: Permissions('co_branded_marketing_collaterals', 'read') === true,
                    isSubMenu: true,
                    subKey: 'mdf_spending',
                    children: [
                        { key: 'mdf-group', title: 'By group', to: '/premier_access_groups_spending' },
                        { key: 'mdf-user', title: 'By user', to: '/premier_access_group_users_spending' },
                    ],
                },
            ],
        },
        {
            key: 'targeting',
            title: 'Targeting',
            items: [
                {
                    key: 'categories',
                    title: 'Categories',
                    icon: <CategoryIcon />,
                    visible: Permissions('categories', 'read') === true,
                    isSubMenu: true,
                    subKey: 'categories',
                    children: [
                        { key: 'cat-product-updates', title: 'Product updates', to: `/taxonomies/${Taxonomies.productNewsAndUpdates}/categories` },
                        { key: 'cat-system', title: 'System and technical', to: `/taxonomies/${Taxonomies.systemAndTechnical}/categories` },
                    ],
                },
                {
                    key: 'platforms',
                    title: 'Product Visibility',
                    to: '/platforms',
                    icon: <ApartmentIcon />,
                    visible: Permissions('products', 'read') === true,
                },
                {
                    key: 'product-pages',
                    title: 'Product Pages',
                    to: '/product_pages',
                    icon: <Inventory2Icon />,
                    visible: Permissions('products', 'read') === true,
                },
            ],
        },
        {
            key: 'reports',
            title: 'Reports',
            items: [
                {
                    key: 'expirations',
                    title: 'Expirations',
                    icon: <ScheduleIcon />,
                    visible: Permissions('page_expirations', 'read') === true,
                    isSubMenu: true,
                    subKey: 'expirations',
                    children: [
                        { key: 'expirations-page', title: 'By page', to: '/page_expirations' },
                        { key: 'expirations-item', title: 'By item', to: '/item_expirations' },
                    ],
                },
                {
                    key: 'tracked-downloads',
                    title: 'Download activity',
                    to: '/tracked_downloads',
                    icon: <DownloadIcon />,
                    visible: Permissions('tracked_downloads', 'read') === true,
                },
                {
                    key: 'announcements-exports',
                    title: 'Announcements Reports',
                    to: '/announcements_exports',
                    icon: <AssessmentIcon />,
                    visible: Permissions('announcements_exports', 'read') === true,
                },
                {
                    key: 'contact-forms-group',
                    title: 'Contact form submissions',
                    icon: <ContactMailIcon />,
                    visible: Permissions('form_entries', 'read') === true,
                    isSubMenu: true,
                    subKey: 'contact_submissions',
                    children: [
                        { key: 'contact-forms', title: 'Contact forms', to: '/contact_forms' },
                        { key: 'holiday-gifts', title: 'Holiday gift requests', to: '/holiday_gift_requests' },
                    ],
                },
            ],
        },
        {
            key: 'people',
            title: 'People',
            items: [
                {
                    key: 'agents',
                    title: 'Agents',
                    to: '/agents',
                    icon: <PersonIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'agencies',
                    title: 'Agencies',
                    to: '/agencies',
                    icon: <BusinessIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'agency-locations',
                    title: 'Agency locations',
                    to: '/agency_locations',
                    icon: <PlaceIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'premier-access-groups',
                    title: 'Premier Access groups',
                    to: '/premier_access_groups',
                    icon: <WorkspacesIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'premier-designations',
                    title: 'Premier designations',
                    to: '/premier_access_groups_imports',
                    icon: <StarIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'customer-lists',
                    title: 'Customer lists',
                    to: '/customer_lists',
                    icon: <GroupsIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'employees',
                    title: 'Employees',
                    to: '/employees',
                    icon: <BadgeIcon />,
                    visible: Permissions('users', 'read') === true,
                },
                {
                    key: 'admins',
                    title: 'Administrators',
                    to: '/admins',
                    icon: <AdminPanelSettingsIcon />,
                    visible: Permissions('users', 'read') === true,
                },
            ],
        },
        {
            key: 'integrations',
            title: 'Integrations',
            items: [
                {
                    key: 'agent-data-feed',
                    title: 'Agent data feed',
                    icon: <SyncIcon />,
                    visible: Permissions('users', 'read') === true,
                    isSubMenu: true,
                    subKey: 'agent_feed',
                    children: [
                        { key: 'authority-override', title: 'Agent authority override', to: '/user_products_authority_imports' },
                        { key: 'vue-imports', title: 'VUE file imports', to: '/import_users_histories' },
                    ],
                },
                {
                    key: 'mulesoft-exports',
                    title: 'AI content feed',
                    to: '/mulesoft_exports',
                    icon: <SmartToyIcon />,
                    visible: Permissions('mulesoft_exports', 'read') === true,
                },
            ],
        },
        {
            key: 'settings',
            title: 'Settings',
            items: [
                {
                    key: 'sessions',
                    title: 'Session timeouts',
                    to: '/session_timeouts',
                    icon: <DevicesIcon />,
                    visible: Permissions('settings', 'read') === true,
                },
                {
                    key: 'roles',
                    title: 'Access and permissions',
                    to: '/roles',
                    icon: <ShieldIcon />,
                    visible: Permissions('roles', 'read') === true,
                },
            ],
        },
    ];

    return sections;
};

// Landing page: featured content when the admin may open it, otherwise the first menu entry they are allowed to open
export const getFirstAllowedPath = () => {
    const allowedPaths = [];

    buildMenuSections().forEach((section) => {
        section.items.forEach((item) => {
            if (!item.visible) return;

            if (item.isSubMenu) {
                item.children.forEach((child) => allowedPaths.push(child.to));

                return;
            }

            if (item.to) {
                allowedPaths.push(item.to);
            }
        });
    });

    if (allowedPaths.includes(PREFERRED_LANDING_PATH)) {
        return PREFERRED_LANDING_PATH;
    }

    return allowedPaths.length > 0 ? allowedPaths[0] : UNAUTHORIZED_PATH;
};

const MenuCustom = ({ onMenuClick }) => {
    const [sidebarOpen] = useSidebarState();
    const [filterText, setFilterText] = useState('');
    const [collapsedSections, setCollapsedSections] = useState({});
    const [openSubMenus, setOpenSubMenus] = useState({ homepage: true });

    const toggleSection = (sectionKey) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }));
    };

    const handleSubMenuClick = (item) => {
        setOpenSubMenus((prev) => ({
            ...prev,
            [item]: !prev[item],
        }));
    };

    // Helper to wrap single item with tooltip in collapsed rail mode
    const renderMenuItem = (item) => {
        if (!item.visible) return null;

        if (!sidebarOpen) {
            return (
                <Tooltip key={item.key} placement="right" title={item.title}>
                    <Box component="div" sx={{ display: 'flex', justifyContent: 'center' }}>
                        <StyledMenuItemLink
                            key={item.key}
                            leftIcon={item.icon}
                            primaryText=""
                            sidebarOpen={sidebarOpen}
                            to={item.to}
                            onClick={onMenuClick}
                        />
                    </Box>
                </Tooltip>
            );
        }

        const primaryContent = item.badge ? (
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box
                    component="span"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {item.title}
                </Box>
                <Box component="span" sx={{ ml: 'auto', pl: 0.75, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
                    {item.badge}
                </Box>
            </Box>
        ) : (
            item.title
        );

        return (
            <StyledMenuItemLink
                key={item.key}
                leftIcon={item.icon}
                primaryText={primaryContent}
                sidebarOpen={sidebarOpen}
                to={item.to}
                onClick={onMenuClick}
            />
        );
    };

    const sections = buildMenuSections();

    // Collect searchable items for Command Palette
    const searchableItems = [];
    sections.forEach((sec) => {
        sec.items.forEach((it) => {
            if (!it.visible) return;
            if (it.isSubMenu && it.children) {
                it.children.forEach((c) => {
                    searchableItems.push({
                        title: c.title,
                        section: `${sec.title} / ${it.title}`,
                        to: c.to,
                        icon: it.icon,
                    });
                });
            } else if (it.to) {
                searchableItems.push({
                    title: it.title,
                    section: sec.title,
                    to: it.to,
                    icon: it.icon,
                });
            }
        });
    });

    const q = filterText.toLowerCase().trim();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                backgroundColor: 'background.paper',
            }}
        >
            {/* Top Brand Header */}
            <Logo sidebarOpen={sidebarOpen} />

            {/* In-sidebar Navigation Filter (visible when expanded) */}
            {sidebarOpen ? (
                <Box sx={{ px: '12px', pt: '6px', pb: '8px' }}>
                    <NavSearch
                        value={filterText}
                        searchableItems={searchableItems}
                        onChange={setFilterText}
                    />
                </Box>
            ) : null}

            {/* Scrollable Navigation Items */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    py: sidebarOpen ? '10px' : 0.5,
                    px: sidebarOpen ? '8px' : 0,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                        borderRadius: '6px',
                    },
                }}
            >
                {sections.map((section) => {
                    const visibleItems = section.items.filter((item) => item.visible);
                    if (visibleItems.length === 0) return null;

                    // Apply live filtering if search text is present
                    const matchingItems = visibleItems.filter((item) => {
                        if (!q) return true;
                        if (item.title.toLowerCase().includes(q)) return true;
                        if (item.isSubMenu && item.children) {
                            return item.children.some((c) => c.title.toLowerCase().includes(q));
                        }

                        return false;
                    });

                    if (q && matchingItems.length === 0) return null;

                    const isSectionOpen = q ? true : !collapsedSections[section.key];

                    return (
                        <MenuSection key={section.key}>
                            <SectionHeader
                                title={section.title}
                                isOpen={isSectionOpen}
                                sidebarOpen={sidebarOpen}
                                onToggle={() => toggleSection(section.key)}
                            />

                            {isSectionOpen ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    {matchingItems.map((item) => {
                                        if (item.isSubMenu) {
                                            const isSubOpen = q ? true : Boolean(openSubMenus[item.subKey]);
                                            const subChildren = item.children.filter((child) => {
                                                if (!q) return true;
                                                return child.title.toLowerCase().includes(q) || item.title.toLowerCase().includes(q);
                                            });

                                            return (
                                                <SubMenu
                                                    key={item.key}
                                                    currentItem={isSubOpen ? item.subKey : null}
                                                    item={item.subKey}
                                                    title={item.title}
                                                    icon={item.icon}
                                                    sidebarOpen={sidebarOpen}
                                                    badge={item.badge}
                                                    onClick={() => handleSubMenuClick(item.subKey)}
                                                >
                                                    {subChildren.map((child) => (
                                                        <StyledSubMenuItemLink
                                                            key={child.key}
                                                            primaryText={child.title}
                                                            to={child.to}
                                                            onClick={onMenuClick}
                                                        />
                                                    ))}
                                                </SubMenu>
                                            );
                                        }

                                        return renderMenuItem(item);
                                    })}
                                </Box>
                            ) : null}
                        </MenuSection>
                    );
                })}
            </Box>

            {/* Pinned Bottom Account Footer */}
            <NavAccountFooter sidebarOpen={sidebarOpen} />
        </Box>
    );
};

MenuCustom.propTypes = {
    onMenuClick: PropTypes.func,
};

export default React.memo(MenuCustom);
